import { DestroyRef, Directive, ElementRef, inject, OnInit, output } from '@angular/core';

/**
 * Avisa **uma única vez** quando o elemento entra na área visível, para adiar trabalho caro até que
 * ele realmente seja olhado.
 *
 * Nasceu do quadro kanban: o backlog pode ter centenas de cards, e buscar a foto de cada médico no
 * carregamento significaria centenas de requisições — a maioria de cards que o usuário nunca rola
 * até ver. Aqui a busca acontece por card, quando o card aparece.
 *
 * `IntersectionObserver` respeita o recorte de qualquer ancestral com `overflow`, então funciona
 * tanto para a rolagem da página quanto para a rolagem de dentro da coluna.
 */
@Directive({
    selector: '[appAoAparecer]',
    standalone: true
})
export class AoAparecerDirective implements OnInit {
    private readonly elemento = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly destroyRef = inject(DestroyRef);

    /** Emitido na primeira vez que o elemento aparece. Nunca repete. */
    readonly appAoAparecer = output<void>();

    private jaAvisou = false;

    ngOnInit(): void {
        // Ambiente sem IntersectionObserver (SSR, navegador antigo): emite de imediato. Perder o
        // adiamento é aceitável; deixar o conteúdo nunca carregar, não.
        if (typeof IntersectionObserver === 'undefined') {
            this.avisar();
            return;
        }

        const observador = new IntersectionObserver((entradas) => {
            if (!entradas.some((entrada) => entrada.isIntersecting)) {
                return;
            }
            // Desconecta ANTES de avisar: o observador dispara a cada reentrada na tela, e sem isso
            // rolar a lista para frente e para trás repetiria o trabalho que se quis adiar.
            observador.disconnect();
            this.avisar();
        });

        observador.observe(this.elemento.nativeElement);
        this.destroyRef.onDestroy(() => observador.disconnect());
    }

    /**
     * Emite no maximo uma vez. A guarda existe porque o observador pode entregar mais de uma entrada
     * intersectando no mesmo disparo, e porque o caminho sem `IntersectionObserver` avisa direto —
     * quem escuta trata o aviso como "carregue isto agora", e repetir seria refazer o trabalho.
     */
    private avisar(): void {
        if (this.jaAvisou) {
            return;
        }
        this.jaAvisou = true;
        this.appAoAparecer.emit();
    }
}
